import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CLIENT_CONFIG } from '@config/config';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { AmplifyAuthService } from '@app-auth/services/amplify-auth/amplify-auth.service';
import { TranslateService } from '@ngx-translate/core';
import { SUPPORTED_LANGUAGES } from '@app-core/constants/constants';
import { takeUntil } from 'rxjs/operators';
import { LoginPageUtilService } from '@app-auth/services/login-page-util.service';
import { CognitoRegionType } from '@app-core/models/core.model';
import { MatButton } from '@angular/material/button';

export const LoginFormMode: { [key: string]: LoginFormModeType } = {
  LOGIN: 'login',
  FORGOT_PASSWORD: 'forgotPassword',
  NEW_PASSWORD: 'newPassword',
  PASSWORD_CHANGE_SUCCESS: 'passwordChangeSuccess',
  MFA: 'mfa',
};

export type LoginFormModeType = 'login' | 'forgotPassword' | 'newPassword' | 'passwordChangeSuccess' | 'mfa';

export const SubmitButtonLabel = {
  [LoginFormMode.LOGIN]: 'customLoginSignInButton',
  [LoginFormMode.FORGOT_PASSWORD]: 'customLoginForgotPasswordButton',
  [LoginFormMode.NEW_PASSWORD]: 'customLoginNewPasswordButton',
  [LoginFormMode.MFA]: 'customLoginSignInButton',
};

interface RegionDetails {
  showRegionSelection: boolean;
  regions: CognitoRegionType[];
  previouslySelectedRegion: string;
  disableRegionSelction: boolean;
}

@Component({
  selector: 'app-custom-login',
  templateUrl: './custom-login.component.html',
  styleUrls: ['./custom-login.component.scss'],
})
export class CustomLoginComponent implements OnInit, OnDestroy {
  public loginForm: FormGroup;
  public config = CLIENT_CONFIG;
  public loginFormMode = LoginFormMode;
  public submitButtonLabel = SubmitButtonLabel;
  public showBackButton: boolean = false;
  public tempPasswordFlow: boolean = false;
  public regionDetails: RegionDetails = {
    showRegionSelection: false,
    regions: [],
    previouslySelectedRegion: '',
    disableRegionSelction: this.config.landingPageDetails?.disableRegionSelection,
  };

  private mode = new BehaviorSubject<LoginFormModeType>(LoginFormMode.LOGIN);
  public mode$: Observable<LoginFormModeType> = this.mode.asObservable();

  public supportedLanguages = SUPPORTED_LANGUAGES;
  public languageControl = new FormControl('');
  public passwordRulesKeys = ['minLength', 'lowercase', 'uppercase', 'number', 'specialChar', 'noEdgeSpaces', 'passwordsMatch'];
  public passwordRules: { [key: string]: boolean } = {};
  public showErrMsg: boolean = false;
  public errMsg: string = '';
  public loader: boolean = false;
  private destroy$ = new Subject<void>();

  private readonly MAX_AUTOFILL_ATTEMPTS = 30;
  private attempts = 0;

  @ViewChild('usernameInput', { static: false }) usernameInput: ElementRef;
  @ViewChild('pwdInput', { static: false }) pwdInput: ElementRef;
  @ViewChild('submitButton', { static: false }) submitButton: MatButton;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private snackbarService: SnackBarService,
    private amplifyAuth: AmplifyAuthService,
    private loginUtilService: LoginPageUtilService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.subscribeToPasswordChanges();
    this.subscribeToModeChanges();
    this.getLanguageValue();
    this.getRegionValue();
    this.enableSubmitAfterAutofill();
  }

  createForm() {
    this.loginForm = this.fb.group(
      {
        region: this.fb.control({
          value: this.regionDetails.previouslySelectedRegion,
          disabled: this.regionDetails.disableRegionSelction,
        }),
        username: ['', [Validators.required]],
        password: ['', [Validators.required]],
        code: ['', [Validators.required]],
        newPassword: ['', [Validators.required]],
        confirmPassword: ['', [Validators.required]],
        totp: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator('newPassword', 'confirmPassword') }
    );
  }

  private passwordMatchValidator(newPasswordKey: string, confirmPasswordKey: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const password = formGroup.get(newPasswordKey)?.value;
      const confirmPassword = formGroup.get(confirmPasswordKey)?.value;

      if (password !== confirmPassword) {
        this.passwordRules.passwordsMatch = false;
      } else {
        this.passwordRules.passwordsMatch = true;
      }

      return null;
    };
  }

  subscribeToPasswordChanges() {
    this.loginForm
      .get('newPassword')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        const value = val || '';
        this.passwordRules = {
          ...this.passwordRules,
          minLength: value.length >= 9,
          lowercase: /[a-z]/.test(value),
          uppercase: /[A-Z]/.test(value),
          number: /\d/.test(value),
          specialChar: /[\^\$\*\.\[\]\{\}\(\)\?\-"!@#%&\/\\,><':;|_~`+=]/.test(value),
          noEdgeSpaces: value && value === value.trim(),
        };
      });
  }

  subscribeToModeChanges() {
    this.mode$.pipe(takeUntil(this.destroy$)).subscribe((mode: LoginFormModeType) => {
      this.updateValidity(true); // Clear validators first

      switch (mode) {
        case LoginFormMode.LOGIN:
          this.regionDetails.showRegionSelection && this.loginForm.get('region').setValidators([Validators.required]);
          this.loginForm.get('username').setValidators([Validators.required]);
          this.loginForm.get('password').setValidators([Validators.required]);
          this.showBackButton = false;
          break;

        case LoginFormMode.FORGOT_PASSWORD:
          this.loginForm.get('username').setValidators([Validators.required]);
          this.showBackButton = true;
          break;

        case LoginFormMode.NEW_PASSWORD:
          this.loginForm.get('username').setValidators([Validators.required]);
          !this.tempPasswordFlow && this.loginForm.get('code').setValidators([Validators.required]);
          this.loginForm.get('newPassword').setValidators([Validators.required]);
          this.loginForm.get('confirmPassword').setValidators([Validators.required]);
          this.showBackButton = true;
          break;

        case LoginFormMode.PASSWORD_CHANGE_SUCCESS:
          break;

        case LoginFormMode.MFA:
          this.loginForm.get('totp').setValidators([Validators.required]);
          this.showBackButton = true;
          break;

        default:
          console.error('Unknown mode:', mode);
      }

      this.updateValidity();
    });
  }

  private updateValidity(clearValidators: boolean = false) {
    Object.values(this.loginForm.controls).forEach((control) => {
      clearValidators && control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
    });
    this.loginForm.updateValueAndValidity({ emitEvent: false });
    this.resetErrMsg();
  }

  public goBack() {
    switch (this.mode.value) {
      case LoginFormMode.FORGOT_PASSWORD:
        this.mode.next(LoginFormMode.LOGIN);
        break;

      case LoginFormMode.NEW_PASSWORD:
        this.loginForm.get('code').reset();
        this.loginForm.get('newPassword').reset();
        this.loginForm.get('confirmPassword').reset();
        this.loginForm.get('confirmPassword').setErrors(null);
        this.mode.next(LoginFormMode.FORGOT_PASSWORD);
        break;

      case LoginFormMode.MFA:
        this.mode.next(LoginFormMode.LOGIN);
        break;
    }
  }

  async onSubmit() {
    this.loader = true;

    switch (this.mode.value) {
      case LoginFormMode.LOGIN:
        await this.onSignIn();
        break;

      case LoginFormMode.FORGOT_PASSWORD:
        await this.onForgotPassword();
        break;

      case LoginFormMode.NEW_PASSWORD:
        this.tempPasswordFlow ? await this.onConfirmSignIn(this.loginForm.value.newPassword) : await this.onNewPassword();
        break;

      case LoginFormMode.MFA:
        await this.onConfirmSignIn(this.loginForm.value.totp);
        break;

      default:
        console.error('Unknown mode:', this.mode);
        break;
    }

    this.loader = false;
  }

  async onSignIn() {
    try {
      const { signInStep, tokens } = await this.amplifyAuth.signIn(this.loginForm.value.username.trim(), this.loginForm.value.password);

      if (signInStep === 'DONE') {
        const accessToken = tokens?.accessToken?.toString();
        this.router.navigate(['/callback'], { queryParams: { source: 'customLoginPage' }, state: { token: accessToken } });
      } else if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        this.tempPasswordFlow = true;
        this.mode.next(LoginFormMode.NEW_PASSWORD);
      } else if (signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        this.mode.next(LoginFormMode.MFA);
      }
    } catch (error) {
      const message = error?.customMessage || 'customLoginSignInFailed';
      this.showErrorMsg(message);
    }
  }

  async onConfirmSignIn(confirmSignInInput: string) {
    try {
      const { signInStep, tokens } = await this.amplifyAuth.confirmSignIn(confirmSignInInput);

      if (signInStep === 'DONE') {
        const accessToken = tokens?.accessToken?.toString();
        this.router.navigate(['/callback'], { queryParams: { source: 'customLoginPage' }, state: { token: accessToken } });
      }
    } catch (error) {
      const message = error?.customMessage || 'customLoginSignInFailed';
      this.showErrorMsg(message);
    }
  }

  goToForgotPasswordPage() {
    if (this.loginForm?.get('region')?.value) {
      this.loginForm.get('password').reset();
      this.tempPasswordFlow = false;
      this.mode.next(LoginFormMode.FORGOT_PASSWORD);
    } else {
      this.snackbarService.failure(this.translate.instant('customLoginSelectRegionError'));
      this.showErrorMsg('customLoginSelectRegionError');
    }
  }

  async onForgotPassword() {
    try {
      await this.amplifyAuth.forgotPassword(this.loginForm.value.username);
      this.mode.next(LoginFormMode.NEW_PASSWORD);
    } catch (error) {
      const message = error?.customMessage || 'customLoginSignInFailed';
      this.showErrorMsg(message);
    }
  }

  async onNewPassword() {
    try {
      await this.amplifyAuth.handleSubmitNewPassword(
        this.loginForm.value.username,
        this.loginForm.value.code,
        this.loginForm.value.newPassword
      );

      this.mode.next(LoginFormMode.PASSWORD_CHANGE_SUCCESS);
      this.loginForm.reset();
      //go back to login and patch region control
      const timer = setTimeout(() => {
        this.mode.next(LoginFormMode.LOGIN);
        this.regionDetails.previouslySelectedRegion &&
          this.loginForm.get('region')?.patchValue(this.regionDetails.previouslySelectedRegion);
      }, 5000);

      this.destroy$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        clearTimeout(timer);
      });
    } catch (error) {
      const message = error?.customMessage || 'customLoginSignInFailed';
      this.showErrorMsg(message);
    }
  }

  public getLanguageValue() {
    const currentLanguage = this.loginUtilService.getCurrentLanguage();
    this.languageControl.patchValue(currentLanguage);

    this.languageControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedLanguage) => {
      if (selectedLanguage) {
        this.loginUtilService.languageChanged(selectedLanguage);
      }
    });
  }

  public getRegionValue() {
    this.regionDetails.showRegionSelection = this.loginUtilService.showRegionSelection;
    ({ regions: this.regionDetails.regions, previouslySelectedRegion: this.regionDetails.previouslySelectedRegion } =
      this.loginUtilService.getRegionValue());

    const regionControl = this.loginForm.get('region');
    this.regionDetails.showRegionSelection && regionControl.setValidators([Validators.required]);

    regionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedRegion) => {
      this.loginUtilService.regionChanged(selectedRegion);
      this.amplifyAuth.configureAWSAmplify();
    });

    this.regionDetails.previouslySelectedRegion && regionControl.patchValue(this.regionDetails.previouslySelectedRegion);
  }

  get isSubmitDisabled() {
    const invalid = !this.loginForm?.valid;
    const invaildPassword = Object.values(this.passwordRules).some((val) => val === false);
    const isDisabled = this.mode.value === LoginFormMode.NEW_PASSWORD ? invalid || invaildPassword : invalid;
    return isDisabled;
  }

  private resetErrMsg() {
    this.showErrMsg = false;
    this.errMsg = '';
  }

  private showErrorMsg(message: string) {
    this.showErrMsg = true;
    this.errMsg = message;
  }

  private enableSubmitAfterAutofill(): void {
    if (this.handleAutofill() || this.attempts >= this.MAX_AUTOFILL_ATTEMPTS) {
      return; // Stop the loop if autofill is handled or max attempts are reached
    }

    this.attempts++;

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => this.enableSubmitAfterAutofill());
    } else {
      setTimeout(() => this.handleAutofill(), 200);
    }
  }

  private handleAutofill(): boolean {
    const region = this.loginForm.get('region')?.value;
    const isUsernameAutofilled = this.usernameInput?.nativeElement?.classList.contains('cdk-text-field-autofilled');
    const isPasswordAutofilled = this.pwdInput?.nativeElement?.classList.contains('cdk-text-field-autofilled');

    if (region && isUsernameAutofilled && isPasswordAutofilled) {
      if (this.submitButton) {
        this.submitButton.disabled = false; // Enable the submit button
      }
      return true; // Autofill handled successfully
    }

    return false; // Autofill not yet handled
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
